import { PACK_IDS } from '../shared/game';
import { HudFrame } from './HudFrame';

type HelpOverlayProps = {
  connected: boolean;
  compact?: boolean;
  minimal?: boolean;
};

function frameClip() {
  return 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))';
}

function KeyCap({ label, compact = false }: { label: string; compact?: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: compact || label.length > 3 ? 30 : 36,
        padding: compact ? '3px 6px' : '4px 8px',
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.10)',
        clipPath: frameClip(),
        WebkitClipPath: frameClip(),
        color: '#f4fbff',
        fontSize: compact ? 9 : 10,
        letterSpacing: compact ? 1 : 1.2,
        textTransform: 'uppercase',
      }}
    >
      {label}
    </span>
  );
}

const PACK_SLOT_KEYS = PACK_IDS.map((_, index) => String(index + 5)).join('-');

const CONTROL_ROWS = [
  { key: '1-4', label: 'Quick-select weapons; mortar slot is heavy armor only' },
  { key: 'WASD', label: 'Drive lane line and keep heading through chokes' },
  { key: 'SPACE', label: 'Tap for pop-jump, hold for jet carry over crest lines' },
  { key: 'LMB', label: 'Primary fire; pace shots around heat and cycle window' },
  { key: 'RMB', label: 'Hold ADS for tighter reticle and precision camera follow' },
  { key: 'Q / E', label: `Cycle packs while moving; ${PACK_SLOT_KEYS} for direct slot picks` },
  { key: 'R', label: 'Activate the selected pack before commit or disengage' },
  { key: 'F', label: 'Throw the selected grenade on commit or for mobility lines' },
  { key: 'H', label: 'Cycle grenade archetypes between frag and concussion' },
  { key: 'F1-F3', label: 'Armor swap at a powered inventory station' },
  { key: 'Z X C V', label: 'Call team directive (Attack / Defend / Regroup / Support)' },
  { key: 'MMB / G', label: 'Mark immediate focus point with tactical ping' },
  { key: 'CLICK', label: 'Lock pointer and keep full mouse look control' },
] as const;

const COMMAND_REFERENCE = [
  { key: 'Z', label: 'Attack route', detail: 'Push objective lane and force base pressure' },
  { key: 'X', label: 'Defend route', detail: 'Stabilize home lane and protect return path' },
  { key: 'C', label: 'Regroup', detail: 'Collapse split pushes and reset timing' },
  { key: 'V', label: 'Support', detail: 'Escort carrier or reinforce active duel' },
  { key: 'MMB / G', label: 'Ping', detail: 'Mark exact lane choke / entry / objective focus' },
] as const;

const ROUTE_CUES = [
  'Open with spinfusor or mortar on contested lane, then swap to chaingun/blaster for cleanup.',
  'Use directives before entry; ping the exact choke you want teammates to collapse on.',
  'If pressure spikes, call defend/regroup and rotate through base-side lane to reset.',
] as const;

const TRAINING_CUE = 'New pilots can launch Training from the main menu for a lightweight checklist covering movement, weapons, packs, deployables, vehicles, and objectives.';

export function HelpOverlay({ connected, compact = false, minimal = false }: HelpOverlayProps) {
  const statusTone = connected ? '#62ddff' : '#ff6f6f';
  const statusText = connected ? 'Arena link established' : 'Waiting for server acknowledgement';
  const sessionTone = connected ? 'rgba(98,221,255,0.14)' : 'rgba(255,111,111,0.14)';
  const sessionBorderTone = connected ? 'rgba(98,221,255,0.24)' : 'rgba(255,111,111,0.22)';

  return (
    <HudFrame
      title="STATUS / CONTROLS"
      subtitle={connected ? statusText : 'Connection lost'}
      tone={connected ? 'cyan' : 'red'}
      trailing={
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '5px 9px',
            background: connected ? 'rgba(98,221,255,0.10)' : 'rgba(255,111,111,0.12)',
            border: connected ? '1px solid rgba(98,221,255,0.22)' : '1px solid rgba(255,111,111,0.22)',
            clipPath: frameClip(),
            WebkitClipPath: frameClip(),
            fontSize: 10.5,
            letterSpacing: 1.2,
            textTransform: 'uppercase',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 6,
              height: 6,
              background: statusTone,
              boxShadow: `0 0 12px ${statusTone}66`,
            }}
          />
          {connected ? 'Online' : 'Offline'}
        </span>
      }
      style={{
        position: compact ? 'relative' : 'absolute',
        left: compact ? undefined : 14,
        top: compact ? undefined : 14,
        width: compact ? '100%' : minimal ? 'min(264px, calc(100vw - 28px))' : 'min(320px, calc(100vw - 28px))',
        minHeight: compact && !minimal ? 214 : undefined,
        maxHeight: compact ? undefined : 'calc(100vh - 28px)',
        overflowY: compact ? undefined : 'auto',
        overscrollBehavior: 'contain',
      }}
    >
      <div style={{ display: 'grid', gap: 10, fontSize: 12, color: 'rgba(231,244,252,0.78)' }}>
        {minimal ? (
          <>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                alignItems: 'center',
                padding: '8px 9px',
                background: sessionTone,
                border: `1px solid ${sessionBorderTone}`,
                clipPath: frameClip(),
                WebkitClipPath: frameClip(),
                color: '#f4fbff',
              }}
            >
              {CONTROL_ROWS.map((control) => (
                <KeyCap key={control.key} label={control.key} compact />
              ))}
            </div>
            <div
              style={{
                display: 'grid',
                gap: 4,
                padding: '8px 9px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                clipPath: frameClip(),
                WebkitClipPath: frameClip(),
              }}
            >
              <span style={{ fontSize: 9.5, letterSpacing: 1.5, textTransform: 'uppercase', color: '#62ddff' }}>Route Calls</span>
              <span style={{ fontSize: 10.5, lineHeight: 1.3, color: 'rgba(231,244,252,0.78)' }}>
                Z/X/C/V set team intent. F throws grenades, H cycles them, and MMB or G pings exact route focus.
              </span>
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gap: 9,
                padding: '9px 10px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                clipPath: frameClip(),
                WebkitClipPath: frameClip(),
              }}
            >
              {CONTROL_ROWS.map((control) => (
                <div
                  key={control.key}
                  style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: 10, alignItems: 'center' }}
                >
                  <KeyCap label={control.key} compact={compact} />
                  <span>{control.label}</span>
                </div>
              ))}
            </div>

            <div
              style={{
                display: 'grid',
                gap: 8,
                padding: '9px 10px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                clipPath: frameClip(),
                WebkitClipPath: frameClip(),
              }}
            >
              <span style={{ fontSize: 10, letterSpacing: 1.7, textTransform: 'uppercase', color: '#62ddff' }}>Command Quick Reference</span>
              {COMMAND_REFERENCE.map((command) => (
                <div
                  key={command.key}
                  style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: 10, alignItems: 'center' }}
                >
                  <KeyCap label={command.key} compact={compact} />
                  <span style={{ lineHeight: 1.32 }}>
                    <strong style={{ color: '#f4fbff', fontSize: 11 }}>{command.label}:</strong>{' '}
                    <span>{command.detail}</span>
                  </span>
                </div>
              ))}
            </div>

            <div
              style={{
                display: 'grid',
                gap: 6,
                padding: '9px 10px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                clipPath: frameClip(),
                WebkitClipPath: frameClip(),
              }}
            >
              <span style={{ fontSize: 10, letterSpacing: 1.7, textTransform: 'uppercase', color: '#62ddff' }}>Route Combat Cues</span>
              {ROUTE_CUES.map((cue, index) => (
                <span key={`${cue}-${index}`} style={{ fontSize: 10.5, lineHeight: 1.35, color: 'rgba(231,244,252,0.78)' }}>
                  {cue}
                </span>
              ))}
            </div>

            <div
              style={{
                padding: '9px 10px',
                background: 'rgba(98,221,255,0.08)',
                border: '1px solid rgba(98,221,255,0.18)',
                clipPath: frameClip(),
                WebkitClipPath: frameClip(),
                color: '#f8fbff',
                lineHeight: 1.4,
              }}
            >
              <strong style={{ color: '#62ddff', fontSize: 10, letterSpacing: 1.7, textTransform: 'uppercase' }}>Training:</strong>{' '}
              <span style={{ fontSize: 10.5 }}>{TRAINING_CUE}</span>
            </div>

            {connected ? (
              <div
                style={{
                  padding: '10px 11px',
                  background: sessionTone,
                  border: `1px solid ${sessionBorderTone}`,
                  clipPath: frameClip(),
                  WebkitClipPath: frameClip(),
                  color: '#f8fbff',
                  lineHeight: 1.45,
                }}
              >
                <span>
                  Keep right hand on fire and camera. Left-hand movement keys maintain drift control while you push speed through
                  route chokes.
                </span>
              </div>
            ) : (
              <div
                style={{
                  padding: '10px 11px',
                  background: 'rgba(98,221,255,0.08)',
                  border: '1px solid rgba(98,221,255,0.18)',
                  clipPath: frameClip(),
                  WebkitClipPath: frameClip(),
                  color: '#f8fbff',
                  lineHeight: 1.45,
                }}
              >
                HUD is passive while disconnected. Inputs and camera control remain fully local until link is restored.
              </div>
            )}
          </>
        )}
      </div>
    </HudFrame>
  );
}
